import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const ServisPage: React.FC = () => {
  const { t } = useTranslation();
  const [selectedService, setSelectedService] = useState<string>('warranty');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      id: 'warranty',
      title: t('servis.warranty'),
      description: t('servis.warrantyDesc'),
      features: [t('servis.warrantyF1'), t('servis.warrantyF2'), t('servis.warrantyF3'), t('servis.warrantyF4')],
      icon: '🛡️',
      responseTime: '24h'
    },
    {
      id: 'postwarranty',
      title: t('servis.postwarranty'),
      description: t('servis.postwarrantyDesc'),
      features: [t('servis.postwarrantyF1'), t('servis.postwarrantyF2'), t('servis.postwarrantyF3'), t('servis.postwarrantyF4')],
      icon: '🔧',
      responseTime: '48h'
    },
    {
      id: 'preventive',
      title: t('servis.preventive'),
      description: t('servis.preventiveDesc'),
      features: [t('servis.preventiveF1'), t('servis.preventiveF2'), t('servis.preventiveF3'), t('servis.preventiveF4')],
      icon: '🔍',
      responseTime: t('servis.plannedTime')
    },
    {
      id: 'emergency',
      title: t('servis.emergencyService'),
      description: t('servis.emergencyServiceDesc'),
      features: [t('servis.emergencyF1'), t('servis.emergencyF2'), t('servis.emergencyF3'), t('servis.emergencyF4')],
      icon: '🚨',
      responseTime: '2h'
    }
  ];

  const serviceLevels = [
    {
      name: 'Basic',
      price: t('servis.basicPrice'),
      features: [
        t('servis.basicF1'),
        t('servis.basicF2'),
        t('servis.basicF3'),
        t('servis.basicF4')
      ],
      recommended: false
    },
    {
      name: 'Professional',
      price: t('servis.professionalPrice'),
      features: [
        t('servis.professionalF1'),
        t('servis.professionalF2'),
        t('servis.professionalF3'),
        t('servis.professionalF4'),
        t('servis.professionalF5'),
        t('servis.professionalF6')
      ],
      recommended: true
    },
    {
      name: 'Enterprise',
      price: t('servis.enterprisePrice'),
      features: [
        t('servis.enterpriseF1'),
        t('servis.enterpriseF2'),
        t('servis.enterpriseF3'),
        t('servis.enterpriseF4'),
        t('servis.enterpriseF5'),
        t('servis.enterpriseF6'),
        t('servis.enterpriseF7')
      ],
      recommended: false
    }
  ];

  return (
    <div className="pt-32 md:pt-44">
      {/* Hero section for Servis page */}
      <section className="section-padding pb-0">
        <div className="container-custom text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-4 md:mb-6">
            {t('servis.title1')} <span className="text-accent-green">{t('servis.title2')}</span>
          </h1>
          <p className="text-gray-400 text-sm md:text-lg max-w-2xl mx-auto leading-relaxed mb-8">
            {t('servis.description')}
          </p>
        </div>
      </section>

      {/* Emergency call button */}
      <section className="section-padding pt-0">
        <div className="container-custom">
          <div className="text-center mb-12">
            <div className="inline-flex flex-col sm:flex-row items-center gap-4 sm:space-x-4 bg-red-600/10 border border-red-600/30 rounded-lg px-6 py-4">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center animate-pulse flex-shrink-0">
                <span className="text-white text-xl">🚨</span>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-white font-semibold">{t('servis.emergency')}</div>
                <div className="text-gray-300 text-sm">{t('servis.emergencyDesc')}</div>
              </div>
              <button className="btn-primary bg-red-600 hover:bg-red-700 whitespace-nowrap">
                {t('servis.callNow')}
              </button>
            </div>
          </div>

          {/* Service types tabs */}
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-8">
              {t('servis.serviceTypes')}
            </h2>
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {services.map((service) => (
                <button
                  key={service.id}
                  onClick={() => setSelectedService(service.id)}
                  className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-full border transition-all duration-200 text-sm sm:text-base ${
                    selectedService === service.id
                      ? 'bg-accent-green border-accent-green text-tech-dark'
                      : 'border-gray-600 text-gray-300 hover:border-accent-green hover:text-accent-green'
                  }`}
                >
                  <span className="mr-1 sm:mr-2">{service.icon}</span>
                  {service.title}
                </button>
              ))}
            </div>

            {/* Selected service detail */}
            {(() => {
              const service = services.find(s => s.id === selectedService);
              if (!service) return null;

              return (
                <div className="tech-card max-w-4xl mx-auto">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <div className="md:col-span-1 lg:col-span-2">
                      <h3 className="text-2xl font-bold text-white mb-4">{service.title}</h3>
                      <p className="text-gray-300 mb-6 leading-relaxed">{service.description}</p>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        {service.features.map((feature, index) => (
                          <div key={index} className="flex items-center space-x-3">
                            <div className="w-2 h-2 bg-accent-green rounded-full flex-shrink-0"></div>
                            <span className="text-gray-300 text-sm sm:text-base">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <div className="bg-tech-dark rounded-lg p-6 border border-[#2a2a2a]">
                        <div className="text-3xl mb-2">{service.icon}</div>
                        <div className="text-sm text-gray-400 mb-2">{t('servis.responseTime')}</div>
                        <div className="text-2xl font-bold text-accent-green">{service.responseTime}</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Service levels / Servisné zmluvy */}
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-4">
              {t('servis.contracts')}
            </h2>
            <p className="text-base sm:text-lg text-gray-300 max-w-3xl mx-auto text-center mb-12">
              {t('servis.contractsDesc')}
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {serviceLevels.map((level, index) => (
                <div 
                  key={index}
                  className={`tech-card relative ${
                    level.recommended ? 'border-accent-green' : ''
                  }`}
                >
                  {level.recommended && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <div className="bg-accent-green text-tech-dark px-4 py-1 rounded-full text-sm font-semibold">
                        {t('servis.recommended')}
                      </div>
                    </div>
                  )}
                  
                  <div className="text-center mb-6">
                    <h4 className="text-xl font-bold text-white mb-2">{level.name}</h4>
                    <div className="text-accent-green font-semibold">{level.price}</div>
                  </div>
                  
                  <div className="space-y-3">
                    {level.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start space-x-3">
                        <div className="w-5 h-5 bg-accent-green rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-tech-dark text-xs">✓</span>
                        </div>
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <button className={`w-full mt-6 py-3 rounded-full font-medium transition-all duration-200 ${
                    level.recommended
                      ? 'btn-primary'
                      : 'btn-secondary'
                  }`}>
                    {t('servis.selectPlan')}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mb-8">
            <div className="inline-flex flex-col sm:flex-row items-center gap-3 sm:space-x-4 bg-tech-gray rounded-lg px-6 py-4 border border-[#2a2a2a]">
              <span className="text-gray-300 text-sm sm:text-base text-center">{t('servis.ctaText')}</span>
              <a href="/#contact" className="btn-primary text-sm whitespace-nowrap">
                {t('servis.ctaBtn')}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServisPage;
